import { useState } from 'react';
import { AlertTriangle } from 'lucide-react';

import type { StepRow } from '@/types/cascade-report';
import { decimal, fillTone, money, num, pct, price } from '../format';
import { METRICS } from '../metrics';
import { MetricHead } from './MetricHead';

/**
 * O centro do relatório: como cada preço da cascata se comportou.
 *
 * Os steps mortos são o que dá ação imediata — preço tentado que nunca comprou
 * gasta uma requisição e um pedaço do timeout em toda cascata, sem retorno.
 */
export function StepTable({ steps }: { steps: StepRow[] }) {
    // Ligado por padrão: a tabela serve para ler o que está funcionando, e o
    // aviso acima já conta quantos morreram e quanto custaram. Quem quiser ver
    // a lista desmarca.
    const [hideDead, setHideDead] = useState(true);

    if (!steps.length) {
        return (
            <p className="py-8 text-center text-sm text-gray-500">
                Nenhum step registrado neste recorte.
            </p>
        );
    }

    const dead = steps.filter((s) => s.dead);
    const wasted = dead.reduce((total, s) => total + s.attempts, 0);
    const visible = hideDead ? steps.filter((s) => !s.dead) : steps;

    return (
        <div className="space-y-3">
            {dead.length > 0 && (
                <div className="flex flex-wrap items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                    <span className="flex-1">
                        <strong>{dead.length}</strong>{' '}
                        {dead.length === 1 ? 'step nunca comprou' : 'steps nunca compraram'} e
                        consumiram <strong>{num(wasted)}</strong> solicitações. São candidatos a
                        sair da cascata ou a baixar de preço.
                    </span>
                    <label className="flex cursor-pointer select-none items-center gap-1.5 whitespace-nowrap text-xs">
                        <input
                            type="checkbox"
                            checked={hideDead}
                            onChange={(e) => setHideDead(e.target.checked)}
                            className="h-3.5 w-3.5 rounded border-amber-300"
                        />
                        Ocultar mortos
                    </label>
                </div>
            )}

            <div className="overflow-x-auto rounded-lg border border-gray-200">
                <table className="w-full text-sm">
                    <thead className="border-b border-gray-200 bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
                        <tr>
                            <th className="px-4 py-2 text-left font-medium">Preço</th>
                            <MetricHead metric="attempts" />
                            <MetricHead metric="wins" />
                            <MetricHead metric="hit_rate" align="left" />
                            <MetricHead metric="requests_per_sale" />
                            <MetricHead metric="revenue_est" />
                            <MetricHead metric="viewable_rate" />
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                        {visible.map((step) => (
                            <tr
                                key={step.price}
                                className={step.dead ? 'bg-red-50/40' : 'hover:bg-gray-50'}
                            >
                                <td className="px-4 py-2 font-medium tabular-nums text-gray-900">
                                    <span className="flex items-center gap-1.5">
                                        {price(step.price)}
                                        {step.dead && (
                                            <span
                                                className="cursor-help rounded bg-red-100 px-1.5 py-0.5 text-[10px] font-medium uppercase text-red-700"
                                                title={METRICS.dead.long}
                                            >
                                                morto
                                            </span>
                                        )}
                                    </span>
                                </td>
                                <td className="px-4 py-2 text-right tabular-nums text-gray-600">
                                    {num(step.attempts)}
                                </td>
                                <td className="px-4 py-2 text-right tabular-nums text-gray-900">
                                    {num(step.wins)}
                                </td>
                                <td className="px-4 py-2">
                                    <div className="flex items-center gap-2">
                                        <div className="h-1.5 w-16 overflow-hidden rounded-full bg-gray-100">
                                            <div
                                                className="h-full rounded-full bg-blue-500"
                                                style={{
                                                    width: `${Math.min(step.hit_rate, 100)}%`,
                                                }}
                                            />
                                        </div>
                                        <span
                                            className={`tabular-nums text-xs ${fillTone(step.hit_rate)}`}
                                        >
                                            {pct(step.hit_rate)}
                                        </span>
                                    </div>
                                </td>
                                <td className="px-4 py-2 text-right tabular-nums text-gray-600">
                                    {step.wins ? decimal(step.requests_per_sale) : '—'}
                                </td>
                                <td className="px-4 py-2 text-right tabular-nums text-gray-900">
                                    {money(step.revenue_est)}
                                </td>
                                <td className="px-4 py-2 text-right tabular-nums text-gray-600">
                                    {step.wins ? pct(step.viewable_rate) : '—'}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {hideDead && (
                <p className="text-xs text-gray-400">
                    {dead.length} {dead.length === 1 ? 'step morto oculto' : 'steps mortos ocultos'}.
                </p>
            )}
        </div>
    );
}
